import React, {useEffect, useState} from "react";
import {Chart} from "react-google-charts";
import {Skeleton, Box, Typography, Grid} from "@mui/material";
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import {styled} from "@mui/material/styles";
import DateRangePickerComponent from "../../common/CustomDateRangePicker";
import moment from "moment";
import useDashboard from "../../../hooks/useDashboard";
import ReactSelect from "react-select";
import useDashboardResetFilterStore from "../../../zustand/useDashboardResetFilterStore";
import EmptyIcon from "../../../assets/images/empty-box.png";

const BorderLinearProgress = styled(LinearProgress)(({theme}) => ({
    height: 10,
    borderRadius: 4,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: theme.palette.mode === 'light' ? '#1a90ff' : '#308fe8',
    },
}));
  
// const options = {
//     colorAxis: {colors: ["#A1ED95", "#22C807", "#4285F4", "#174EA6"]},
//     // region: 'PH', // Focus on the Philippines
//     // displayMode: 'regions',
//     // resolution: 'provinces',
//     // legend: {position: 'none'}
// }

const filterStyles = {
    control: (styles, { isFocused }) => ({
        ...styles,
        textAlign: "left",
        backgroundColor: "#fff",
        minHeight: 20, 
        padding: "0 8px", 
        height: 34, 
        width: 200,
        fontSize: 14,
        borderColor: "rgb(122, 120, 247)",
        '&:hover': {
            borderColor: "rgb(122, 120, 247)", 
        },
    }),
    valueContainer: (styles) => ({
        ...styles,
        padding: "0px 6px",
    }),
    input: (styles) => ({
        ...styles,
        margin: 0,
        padding: 0, 
    }),
    option: (styles) => ({...styles, textAlign: "left", fontSize: 13}),
    dropdownIndicator: (styles) => ({
        ...styles,
        color: "rgb(122, 120, 247)", 
        marginTop: "-2px",
        '&:hover': {
            color: "rgb(122, 120, 247)",
        },
    }),
    clearIndicator: (styles) => ({
        ...styles,
        color: "rgb(122, 120, 247)", 
        marginTop: "-2px",
        '&:hover': {
            color: "rgb(122, 120, 247)", 
        },
    }),
    singleValue: (styles) => ({
        ...styles,
        color: "rgba(51, 51, 207, 0.808)",
        marginTop: "0px",
        fontSize: 13,
    }),
    placeholder: (styles) => ({ ...styles, color: "#3333CFCE", marginTop: "0px", fontSize: 12}), 
    noOptionsMessage: (provided) => ({
        ...provided,
        fontStyle: "italic", 
        textAlign: "start", 
        fontSize: "13px", 
    }),
}

const GeographicDistributionComponent = (props) => {
    const {breakpoints, listOfCountry} = props;

    const {isDashboardResetFilters, setDashboardResetFilters} = useDashboardResetFilterStore();
    const {geolocationDistributionData, isFetchingGeographicDistribution, getGeolocationDistribution, getGeolocationDistributionFilterByCountry} = useDashboard();

    const [startDate, setStartDate] = useState(moment().subtract(6, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [filterType, setFilterType] = useState("country");
    const [selectedCountry, setSelectedCountry] = useState(null);

    const [topGeolocationData, setTopGeolocationData] = useState([]);
    const [topGeolocationDescription, setTopGeolocationDescription] = useState([]);


    useEffect(() => {
        if (isDashboardResetFilters) {
            setStartDate(moment().subtract(6, "days").format("YYYY-MM-DD"));
            setEndDate(moment().format("YYYY-MM-DD"));
            setSelectedCountry(null);
            setFilterType("country");
        }
    }, [isDashboardResetFilters]);

    useEffect(() => {
        if (startDate && endDate) {
            if (filterType === "country" && !selectedCountry)  {
                let filter_type = "country";

                let parameters = {
                    startDate: startDate,
                    endDate: endDate,
                    type: filter_type
                }
                
                getGeolocationDistribution(parameters);
                setDashboardResetFilters(false);

            } else if (filterType === "region" && selectedCountry) {
                let filter_type = "region";

                let parameters = {
                    startDate: startDate,
                    endDate: endDate,
                    type: filter_type,
                    country: selectedCountry?.label
                }

                getGeolocationDistributionFilterByCountry(parameters);
                setDashboardResetFilters(false);
            }
        }
        
    }, [filterType, selectedCountry, startDate, endDate])

    useEffect(() => {
        if (geolocationDistributionData) {
            if (filterType === "country") {
                const convertCountryGeolocationData = (geolocationDistributionData) => {
                    return [
                        ["Country", "Verification"],
                        ...geolocationDistributionData?.map(({country, count}) => [country, count])
                    ];
                }

                setTopGeolocationData(convertCountryGeolocationData(geolocationDistributionData));
                setTopGeolocationDescription(geolocationDistributionData);

            } else if (filterType === "region") {
                const convertRegionGeolocationData = (geolocationDistributionData) => {
                    return [
                        ["Country", "Verification"],
                        ...geolocationDistributionData?.map(({country, count}) => [country, count])
                    ];
                }

                setTopGeolocationData(convertRegionGeolocationData(geolocationDistributionData));
                setTopGeolocationDescription(geolocationDistributionData);
            }
        }
    }, [geolocationDistributionData, filterType])

    


    const handleDateRangeChange = (startDate, endDate) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }

    const handleSelectFilter = (selectedOption) => {
        if (!selectedOption) {
            setSelectedCountry(null);
            setFilterType("country");

        } else {
            setSelectedCountry(selectedOption);
            setFilterType("region");
        }
    }
    // const data = [
    //     ["Country", "Verification"],
    //     ['Germany', 1200],
    //     ['United States',  400],
    //     ['RU', 1500],
    //     ['Canada', 800],
    //     ['France', 1400],
    //     ['Philippines', 1700]
    // ]

    const options = {
        colorAxis: {colors: ["#A1ED95", "#22C807", "#4285F4", "#174EA6"]},
        // region: 'PH',
        // displayMode: 'regions',
        // resolution: 'provinces',
        // legend: {position: 'none'}
    }
    

    return (
        <>
            <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                <Typography sx={{color: "#58565f", fontSize: 18, mb: 3, fontWeight: 600}}>
                    Geographic Distribution
                </Typography>
            </Grid>

            <Grid item xs={8} sm={8} md={8} lg={8} xl={8} sx={{textAlign: "end", justifyContent: "end", ml: "auto", display: "flex", mt: -6}}>  
                <ReactSelect
                    isRtl={false}
                    options={listOfCountry?.map(country => ({
                        label: country.country,
                        value: country.country
                    }))}
                    value={selectedCountry}
                    onChange={selectedOption => handleSelectFilter(selectedOption)}
                    styles={filterStyles}
                    isClearable={true}
                    placeholder={(selectedCountry) ? null : "Select Country"}
                    noOptionsMessage={() => "No matching countries found."} 
                />

                &nbsp; &nbsp;

                <DateRangePickerComponent 
                    initialStartDate={startDate}
                    initialEndDate={endDate}
                    ranges={{
                        "Last 7 Days": [moment().subtract(6, 'days'), moment()],
                        "This Month": [moment().startOf('month'), moment().endOf('month')],
                        "Last Month": [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                        "Last 90 Days": [moment().subtract(89, 'days'), moment()],
                    }}
                    onDateRangeChange={handleDateRangeChange}
                />
            </Grid>

            <Grid container sx={{display: "flex", padding: "0px 24px 24px"}}>
                <Grid item xs={6} sm={7} md={7} lg={7} xl={7}>
                    {isFetchingGeographicDistribution ?     
                        <Box sx={{height: "500px"}}>
                            <Skeleton variant="rounded" sx={{backgroundColor: "#d8d8fdbd"}} height={"100%"} />
                        </Box>
                    :
                        (!isFetchingGeographicDistribution && topGeolocationDescription?.length === 0) ?
                            <Box sx={{textAlign: "end", height: "500px", }}> 
                                <Box sx={{mr: "88px"}}>
                                    <img alt="empty-data-icon" src={EmptyIcon} width="140" style={{paddingTop: "150px"}} />
                                </Box>

                                <Typography sx={{color: "#58565f", fontSize: 14, mt: 1}}>
                                    No data available for the selected date range
                                </Typography>
                            </Box>
                    :   
                        <Box sx={{height: "500px", display: "block", justifyContent: "center", alignItems: "center", border: "1px solid #7A78F74F", p: 4}}>
                            <Chart
                                chartType="GeoChart"
                                data={topGeolocationData}
                                options={options}
                                width="100%"
                                height="100%"
                                className="geo-chart"
                            />
                        </Box>
                    }
                </Grid>

                <Grid item xs={6} sm={5} md={5} lg={5} xl={5}>
                    <Box sx={{ml: 5, mt: (isFetchingGeographicDistribution) ? 8.5 : 10}}>
                        {isFetchingGeographicDistribution ?     
                            <Box sx={{display: "block"}}>
                                <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                                <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                                <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd", mt: 3}} />
                                <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                                <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd", mt: 3}} />
                                <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                                <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd", mt: 3}} />
                                <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                                <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd", mt: 3}} />
                                <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                            </Box> 
                        :
                            (!isFetchingGeographicDistribution && topGeolocationDescription?.length === 0) ?
                                <></>
                        :
                            <>
                                <Typography sx={{color: "#58565f", fontSize: 16, mb: 3, fontWeight: 600}}>
                                    Top Countries
                                </Typography>

                                <Box>
                                    {topGeolocationDescription?.map(({country, count}, index) => (
                                        <Box key={index} sx={{ mt: index !== 0 ? 3 : 0 }}>
                                        <Grid container>
                                            <Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
                                            <Typography sx={{ color: "#58565f", fontSize: 16 }}>
                                                {country}
                                            </Typography>
                                            </Grid>
                                            <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
                                            <Typography sx={{ color: "#58565f", fontSize: 16 }}>
                                                {count?.toLocaleString()}
                                            </Typography>
                                            </Grid>
                                        </Grid>
                                        <BorderLinearProgress variant="determinate" value={count} />
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        }
                    </Box>
                </Grid>
            </Grid>
        </>
    )
}

export default GeographicDistributionComponent
